/**
 * Canonical product category labels — Title Case, case-insensitive dedupe.
 * Used by store API (read/write), MCP tools, and storefront filter chips.
 */

function capitalizeSegment(segment: string): string {
  const trimmed = segment.trim();
  if (!trimmed) {
    return trimmed;
  }
  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1).toLowerCase();
}

function titleCaseToken(token: string): string {
  if (token === '&') {
    return '&';
  }
  if (token.includes('&')) {
    return token
      .split('&')
      .map((part) => capitalizeSegment(part))
      .join(' & ');
  }
  if (token.includes('-')) {
    return token
      .split('-')
      .map((part) => capitalizeSegment(part))
      .join('-');
  }
  return capitalizeSegment(token);
}

/** Normalize a single category string to canonical Title Case (e.g. peptides → Peptides). */
export function normalizeProductCategory(
  category: string | null | undefined
): string | undefined {
  if (category == null) {
    return undefined;
  }
  const trimmed = category.trim();
  if (!trimmed) {
    return undefined;
  }
  return trimmed
    .split(/\s+/)
    .map((token) => titleCaseToken(token))
    .join(' ');
}

/** Case-insensitive category equality for filters and chip selection. */
export function productCategoriesMatch(
  left: string | null | undefined,
  right: string | null | undefined
): boolean {
  const a = normalizeProductCategory(left);
  const b = normalizeProductCategory(right);
  if (!a || !b) {
    return false;
  }
  return a.toLowerCase() === b.toLowerCase();
}

/**
 * Collect unique category labels from product rows, merging case variants
 * (peptides + Peptides → Peptides). Returns sorted display labels.
 */
export function collectUniqueProductCategories(
  categories: Iterable<string | null | undefined>
): string[] {
  const byKey = new Map<string, string>();

  for (const raw of categories) {
    const normalized = normalizeProductCategory(raw);
    if (!normalized) {
      continue;
    }
    const key = normalized.toLowerCase();
    if (!byKey.has(key)) {
      byKey.set(key, normalized);
    }
  }

  return Array.from(byKey.values()).sort((a, b) =>
    a.localeCompare(b, undefined, { sensitivity: 'base' })
  );
}
